import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MessageDialog from "@/components/admin/MessageDialog";
import NotesPanel from "@/components/admin/NotesPanel";
import { FileGrid } from "@/components/admin/PrivateFile";
import { SELLER_STATUSES, STATUS_COLORS, titleCase, fmtDate, money, can, MANAGE_SELLERS } from "@/lib/format";
import { toast } from "sonner";
import { Tag, PackagePlus } from "lucide-react";

function Detail({ label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (<div className="py-1.5 border-b border-[var(--border-lch)]"><div className="text-[11px] uppercase tracking-wide text-[var(--muted)]">{label}</div><div className="text-sm text-[var(--text)]">{String(value)}</div></div>);
}

export default function AdminSellers() {
  const { user } = useAdminAuth();
  const editable = can(user?.role, MANAGE_SELLERS);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");

  const load = useCallback(async () => {
    const params = {}; if (q) params.q = q; if (status !== "all") params.status = status;
    const r = await api.get("/admin/seller-intakes", { params });
    setItems(r.data); setSelected((s) => (s ? r.data.find((x) => x.id === s.id) || null : null));
  }, [q, status]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const patch = async (url, body, msg) => { try { await api.patch(url, body); toast.success(msg); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const addNote = async (note) => { await api.post(`/admin/seller-intakes/${selected.id}/note`, { note }); toast.success("Note added"); load(); };
  const convert = async (s) => { try { const r = await api.post(`/admin/seller-intakes/${s.id}/convert`); toast.success("Converted to inventory (hidden draft created)"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  return (
    <div data-testid="admin-sellers">
      <div className="flex items-center gap-2 mb-1"><Tag className="text-purple-600" size={22} /><h1 className="text-2xl font-semibold">Sell / Consign Intakes</h1></div>
      <p className="text-sm text-[var(--text-2)] mb-4">{items.length} intakes</p>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <Input placeholder="Search name, email, brand…" value={q} onChange={(e) => setQ(e.target.value)} className="sm:max-w-xs bg-[var(--panel)]" data-testid="seller-search" />
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="sm:w-56 bg-[var(--panel)]"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{SELLER_STATUSES.map((s) => <SelectItem key={s} value={s}>{titleCase(s)}</SelectItem>)}</SelectContent></Select>
      </div>
      <Card className="border-[var(--border-lch)] overflow-hidden"><div className="overflow-x-auto"><table className="w-full text-sm">
        <thead className="bg-[var(--panel-2)] text-[var(--text-2)] text-xs uppercase"><tr>
          <th className="text-left p-3">Seller</th><th className="text-left p-3">Watch</th><th className="text-left p-3">Intent</th>
          <th className="text-left p-3">Asking</th><th className="text-left p-3">Status</th><th className="text-left p-3">Added</th></tr></thead>
        <tbody>
          {items.map((s) => (
            <tr key={s.id} className="border-t border-[var(--border-lch)] hover:bg-[var(--panel-2)] cursor-pointer" onClick={() => setSelected(s)} data-testid={`seller-row-${s.id}`}>
              <td className="p-3"><div className="font-medium">{s.full_name}</div><div className="text-xs text-[var(--muted)]">{s.email}</div></td>
              <td className="p-3 text-[var(--text)]">{[s.brand, s.model].filter(Boolean).join(" ") || "—"}</td>
              <td className="p-3 text-[var(--text-2)] capitalize">{s.intent || "—"}</td>
              <td className="p-3 text-[var(--text-2)]">{money(s.asking_price) || "—"}</td>
              <td className="p-3"><Badge variant="outline" className={STATUS_COLORS[s.status]}>{titleCase(s.status)}</Badge></td>
              <td className="p-3 text-[var(--text-2)]">{fmtDate(s.created_at)}</td>
            </tr>
          ))}
          {!items.length && <tr><td colSpan={6} className="p-8 text-center text-[var(--muted)]">No intakes found.</td></tr>}
        </tbody></table></div></Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selected && (<>
            <DialogHeader><DialogTitle>{selected.full_name} — {selected.brand} {selected.model}</DialogTitle></DialogHeader>
            <div className="flex flex-wrap gap-2 mb-3">
              <MessageDialog channel="email" to={selected.email} relatedType="seller_intake" relatedId={selected.id} label="Email" />
              <MessageDialog channel="sms" to={selected.phone} relatedType="seller_intake" relatedId={selected.id} label="Text" />
              {editable && selected.status !== "converted" && (
                <Button size="sm" onClick={() => convert(selected)} data-testid="convert-intake-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white"><PackagePlus size={14} className="mr-1" />Convert to Inventory</Button>
              )}
              {selected.converted_watch_id && <Badge variant="outline" className="bg-emerald-50 text-emerald-700">Converted</Badge>}
            </div>
            <div className="grid sm:grid-cols-2 gap-x-6">
              <div>
                <Detail label="Email" value={selected.email} /><Detail label="Phone" value={selected.phone} />
                <Detail label="Location" value={selected.location} /><Detail label="Intent" value={titleCase(selected.intent)} />
                <Detail label="Reference" value={selected.reference_number} /><Detail label="Year" value={selected.year} />
                <Detail label="Condition" value={selected.condition} /><Detail label="Box/Papers" value={selected.box_papers} />
              </div>
              <div>
                <Detail label="Service History" value={selected.service_history} /><Detail label="Purchase History" value={selected.purchase_history} />
                <Detail label="Original Receipt" value={selected.original_receipt === null ? null : (selected.original_receipt ? "Yes" : "No")} />
                <Detail label="Aftermarket Parts" value={selected.aftermarket_parts} />
                <Detail label="Custom Work" value={selected.custom_work} /><Detail label="Known Issues" value={selected.known_issues} />
                <Detail label="Asking Price" value={money(selected.asking_price)} /><Detail label="Payout Timeline" value={selected.payout_timeline} />
              </div>
            </div>
            {(selected.watch_photos?.length > 0 || selected.paperwork_photos?.length > 0) && (
              <div className="mt-4"><Label className="text-xs text-[var(--text-2)] mb-2 block">Submitted Photos</Label>
                <FileGrid files={[...(selected.watch_photos || []), ...(selected.paperwork_photos || [])]} /></div>
            )}
            <div className="mt-4 space-y-1.5"><Label className="text-xs text-[var(--text-2)]">Status</Label>
              <Select value={selected.status} onValueChange={(v) => patch(`/admin/seller-intakes/${selected.id}/status`, { status: v }, "Status updated")}>
                <SelectTrigger className="bg-[var(--panel)]" data-testid="seller-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{SELLER_STATUSES.map((s) => <SelectItem key={s} value={s}>{titleCase(s)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="mt-4"><Label className="text-xs text-[var(--text-2)] mb-2 block">Internal Notes</Label><NotesPanel notes={selected.notes_log} onAdd={addNote} /></div>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
